import { createReducer, on } from '@ngrx/store';

import * as AuthActions from '@core/store/auth/auth.actions';

import { UserPreferencesActions } from './user-preferences.actions';
import {
  UserPreferencesState,
  initialUserPreferencesState
} from './user-preferences.state';

export const userPreferencesReducer = createReducer(
  initialUserPreferencesState,

  on(
    UserPreferencesActions.loadSuccess,
    (state, { preferences }): UserPreferencesState => ({
      ...state,
      preferences,
      loaded: true,
      loadFailed: false,
      persistError: null
    })
  ),

  // A failed load must NOT flip `loaded` to true: persistence stays gated off so
  // we never overwrite existing backend preferences with an empty blob.
  on(
    UserPreferencesActions.loadFailure,
    (state): UserPreferencesState => ({
      ...state,
      preferences: {},
      loaded: false,
      loadFailed: true
    })
  ),

  on(
    UserPreferencesActions.migrateLegacy,
    (state, { migrated }): UserPreferencesState => ({
      ...state,
      preferences: migrated
    })
  ),

  on(
    UserPreferencesActions.setTableConfig,
    (state, { tableId, config }): UserPreferencesState => ({
      ...state,
      preferences: {
        ...state.preferences,
        tables: { ...state.preferences.tables, [tableId]: config }
      }
    })
  ),

  on(
    UserPreferencesActions.setColumnDisplayMode,
    (state, { tableId, columnKey, displayMode }): UserPreferencesState => {
      const existing = state.preferences.tables?.[tableId] ?? [];
      const found = existing.some((c) => c.key === columnKey);
      const config = found
        ? existing.map((c) => (c.key === columnKey ? { ...c, displayMode } : c))
        : [...existing, { key: columnKey, displayMode }];
      return {
        ...state,
        preferences: {
          ...state.preferences,
          tables: { ...state.preferences.tables, [tableId]: config }
        }
      };
    }
  ),

  on(
    UserPreferencesActions.removeTableConfig,
    (state, { tableId }): UserPreferencesState => {
      if (!state.preferences.tables?.[tableId]) return state;
      const { [tableId]: _removed, ...rest } = state.preferences.tables;
      return { ...state, preferences: { ...state.preferences, tables: rest } };
    }
  ),

  on(
    UserPreferencesActions.setCurrency,
    (state, { currency }): UserPreferencesState => ({
      ...state,
      preferences: { ...state.preferences, currency }
    })
  ),

  on(
    UserPreferencesActions.setSoundEnabled,
    (state, { enabled }): UserPreferencesState => ({
      ...state,
      preferences: { ...state.preferences, soundEnabled: enabled }
    })
  ),

  on(
    UserPreferencesActions.setTheme,
    (state, { theme }): UserPreferencesState => ({
      ...state,
      preferences: { ...state.preferences, theme }
    })
  ),

  on(
    UserPreferencesActions.setSidebarStyle,
    (state, { style }): UserPreferencesState => ({
      ...state,
      preferences: { ...state.preferences, sidebarStyle: style }
    })
  ),

  on(
    UserPreferencesActions.setNotificationPreference,
    (state, { notificationType, enabled }): UserPreferencesState => ({
      ...state,
      preferences: {
        ...state.preferences,
        notifications: {
          ...state.preferences.notifications,
          [notificationType]: enabled
        }
      }
    })
  ),

  on(
    UserPreferencesActions.setTimelinePreferences,
    (state, { preferences }): UserPreferencesState => ({
      ...state,
      preferences: {
        ...state.preferences,
        timeline: { ...state.preferences.timeline, ...preferences }
      }
    })
  ),

  on(
    UserPreferencesActions.setPlanPanelPreferences,
    (state, { preferences }): UserPreferencesState => ({
      ...state,
      preferences: {
        ...state.preferences,
        planPanel: { ...state.preferences.planPanel, ...preferences }
      }
    })
  ),

  on(
    UserPreferencesActions.setFindingsListPreferences,
    (state, { preferences }): UserPreferencesState => {
      const current = state.preferences.findingsList;
      return {
        ...state,
        preferences: {
          ...state.preferences,
          findingsList: {
            ...current,
            ...preferences,
            folded: { ...current?.folded, ...preferences.folded }
          }
        }
      };
    }
  ),

  on(
    UserPreferencesActions.togglePlanPanelKpiVisibility,
    (state, { kpiId }): UserPreferencesState => {
      // The panel shows what the overview shows until its first pick, which starts from there.
      const current =
        state.preferences.planPanel?.kpisHidden ?? state.preferences.kpis?.hidden ?? [];
      const kpisHidden = current.includes(kpiId)
        ? current.filter((id) => id !== kpiId)
        : [...current, kpiId];
      return {
        ...state,
        preferences: {
          ...state.preferences,
          planPanel: { ...state.preferences.planPanel, kpisHidden }
        }
      };
    }
  ),

  on(
    UserPreferencesActions.setVisualizationPreferences,
    (state, { entityType, preferences }): UserPreferencesState => {
      const existing = state.preferences.visualization?.[entityType];
      return {
        ...state,
        preferences: {
          ...state.preferences,
          visualization: {
            ...state.preferences.visualization,
            [entityType]: {
              ...existing,
              ...preferences,
              // `defaultOpenViews` is a nested object: shallow-merge it explicitly
              // so a partial update (e.g. only `enabledByDefault`, or a single
              // window) never wipes the sibling window flags for this entity type.
              defaultOpenViews: {
                ...existing?.defaultOpenViews,
                ...preferences.defaultOpenViews
              }
            }
          }
        }
      };
    }
  ),

  on(
    UserPreferencesActions.togglePinnedEntity,
    (state, { entityName }): UserPreferencesState => {
      const current = state.preferences.pinnedEntities ?? [];
      const pinnedEntities = current.includes(entityName)
        ? current.filter((name) => name !== entityName)
        : [...current, entityName];
      return {
        ...state,
        preferences: { ...state.preferences, pinnedEntities }
      };
    }
  ),

  on(
    UserPreferencesActions.reorderPinnedEntities,
    (state, { order }): UserPreferencesState => {
      // `order` only holds the pinned names visible on the page (current dataspace,
      // current search). They swap among the slots they already occupy, so pins
      // that were hidden keep their position.
      const reordered = order.filter((name) =>
        state.preferences.pinnedEntities?.includes(name)
      );
      const moved = new Set(reordered);
      let next = 0;
      const pinnedEntities = (state.preferences.pinnedEntities ?? []).map((name) =>
        moved.has(name) ? reordered[next++] : name
      );
      return {
        ...state,
        preferences: { ...state.preferences, pinnedEntities }
      };
    }
  ),

  on(
    UserPreferencesActions.setReferenceFields,
    (state, { entityType, fields }): UserPreferencesState => ({
      ...state,
      preferences: {
        ...state.preferences,
        referenceFields: { ...state.preferences.referenceFields, [entityType]: fields }
      }
    })
  ),

  on(
    UserPreferencesActions.setEntitiesOrder,
    (state, { order }): UserPreferencesState => ({
      ...state,
      preferences: { ...state.preferences, entitiesOrder: order }
    })
  ),

  on(
    UserPreferencesActions.setExportPreferences,
    (state, { preferences }): UserPreferencesState => ({
      ...state,
      preferences: {
        ...state.preferences,
        export: { ...state.preferences.export, ...preferences }
      }
    })
  ),

  on(
    UserPreferencesActions.setHideInternalPlanningSpaces,
    (state, { hide }): UserPreferencesState => ({
      ...state,
      preferences: { ...state.preferences, hideInternalPlanningSpaces: hide }
    })
  ),

  on(
    UserPreferencesActions.setFilterGrammar,
    (state, { grammar }): UserPreferencesState => ({
      ...state,
      preferences: { ...state.preferences, filterGrammar: grammar }
    })
  ),

  on(
    UserPreferencesActions.setKpiOrder,
    (state, { order }): UserPreferencesState => ({
      ...state,
      preferences: {
        ...state.preferences,
        kpis: { ...state.preferences.kpis, order }
      }
    })
  ),

  on(
    UserPreferencesActions.toggleKpiVisibility,
    (state, { kpiId }): UserPreferencesState => {
      const current = state.preferences.kpis?.hidden ?? [];
      const hidden = current.includes(kpiId)
        ? current.filter((id) => id !== kpiId)
        : [...current, kpiId];
      return {
        ...state,
        preferences: {
          ...state.preferences,
          kpis: { ...state.preferences.kpis, hidden }
        }
      };
    }
  ),

  on(
    UserPreferencesActions.setKpiDecimals,
    (state, { decimals }): UserPreferencesState => ({
      ...state,
      preferences: {
        ...state.preferences,
        kpis: { ...state.preferences.kpis, decimals }
      }
    })
  ),

  on(
    UserPreferencesActions.persistSuccess,
    (state): UserPreferencesState => ({ ...state, persistError: null })
  ),

  on(
    UserPreferencesActions.persistFailure,
    (state, { error }): UserPreferencesState => ({ ...state, persistError: error })
  ),

  // Reset on logout so the next user (within the same JS session) never sees the
  // previous user's preferences before their own load completes.
  on(AuthActions.logout, (): UserPreferencesState => initialUserPreferencesState)
);
